import type { Course } from '@rcs/data-schema';
import type { Vec3 } from '@rcs/domain';
import { applyMove, DEFAULT_SNAP, type MoveConstraint, type SnapSettings } from './constraints';
import {
  canRedo,
  canUndo,
  createHistory,
  execute,
  preview,
  record,
  redo,
  undo,
  type History,
} from './history';
import { renameCourseCommand, setEnvironmentCommand, setVehicleCommand } from './project';
import { updateControlPointCommand, setClosedCommand, type ControlPointPatch } from './properties';
import {
  addControlPointCommand,
  createEditorState,
  findControlPoint,
  moveControlPointCommand,
  removeControlPointCommand,
  selectControlPoint,
  setControlPointPosition,
  type EditorState,
} from './state';

/** 編集の一続き（履歴と、進行中のドラッグ）。UI は、この状態を `reduceSession` で更新する。 */
export interface EditorSession {
  readonly history: History<EditorState>;
  /** ドラッグ中の制御点と、ドラッグを始めたときの位置 */
  readonly drag: { readonly id: string; readonly from: Vec3 } | undefined;
}

/** 編集の操作。 */
export type SessionAction =
  | { readonly type: 'select'; readonly id: string | undefined }
  | { readonly type: 'move-start'; readonly id: string }
  | {
      readonly type: 'move';
      readonly id: string;
      /** カメラ上の平面で得た、移動先の位置（制約・スナップを適用する前） */
      readonly position: Vec3;
      /** 移動の制約（既定：`free`） */
      readonly constraint?: MoveConstraint | undefined;
      /** グリッドスナップ（既定：オフ） */
      readonly snap?: SnapSettings | undefined;
    }
  | { readonly type: 'move-end'; readonly id: string }
  | { readonly type: 'update'; readonly id: string; readonly patch: ControlPointPatch }
  | { readonly type: 'set-closed'; readonly closed: boolean }
  | { readonly type: 'rename'; readonly name: string }
  | { readonly type: 'set-vehicle'; readonly id: string }
  | { readonly type: 'set-environment'; readonly id: string }
  | { readonly type: 'add'; readonly afterId?: string | undefined }
  | { readonly type: 'remove'; readonly id: string }
  | { readonly type: 'undo' }
  | { readonly type: 'redo' }
  | { readonly type: 'replace-course'; readonly course: Course };

/** コースから、編集の一続きを始める。 */
export function createSession(course: Course): EditorSession {
  return { history: createHistory(createEditorState(course)), drag: undefined };
}

/** 現在のエディターの状態。 */
export function sessionState(session: EditorSession): EditorState {
  return session.history.state;
}

/** 取り消せるか・やり直せるか。 */
export function sessionCan(session: EditorSession): { undo: boolean; redo: boolean } {
  return { undo: canUndo(session.history), redo: canRedo(session.history) };
}

/**
 * 操作を適用した、新しい編集の状態を返す（純粋関数）。
 *
 * - `select`：選択（履歴に積まない）
 * - `move-start` / `move` / `move-end`：ドラッグ。移動の制約とスナップは、ドラッグを始めたときの位置を基準に適用する。
 *   途中経過は履歴に積まず、終わりに 1 手として積む
 *   （位置が変わらなければ積まない）
 * - `update`：選択した制御点のプロパティ（位置・バンク角・区間種別とパラメータ）の変更。不正な値は何もしない
 * - `set-closed`：周回コース・開いたコースの切り替え（周回には 3 点以上が必要）
 * - `set-vehicle`：コースの既定の車両の変更（組み込みの車両のみ。見つからない ID・変化なしは何もしない）
 * - `set-environment`：コースの背景（環境）の変更（組み込みの環境・部品の組み合わせのみ。見つからない ID・変化なしは何もしない）
 * - `rename`：コース名の変更（空・100 文字超・変化なしは何もしない）
 * - `add` / `remove`：制御点の追加（選択中の次、なければ末尾）・削除。数の上限・下限では何もしない
 * - `undo` / `redo`：取り消し・やり直し。ドラッグ中は無視する
 * - `replace-course`：コースを丸ごと置き換える（履歴は作り直す）
 */
export function reduceSession(session: EditorSession, action: SessionAction): EditorSession {
  const { history, drag } = session;
  switch (action.type) {
    case 'select':
      return {
        ...session,
        history: preview(history, selectControlPoint(history.state, action.id)),
      };
    case 'move-start': {
      const point = findControlPoint(history.state, action.id);
      return point ? { ...session, drag: { id: action.id, from: point.position } } : session;
    }
    case 'move':
      if (drag?.id !== action.id) return session;
      return {
        ...session,
        history: preview(
          history,
          setControlPointPosition(
            history.state,
            action.id,
            applyMove(
              drag.from,
              action.position,
              action.constraint ?? 'free',
              action.snap ?? DEFAULT_SNAP,
            ),
          ),
        ),
      };
    case 'move-end': {
      if (drag?.id !== action.id) return session;
      const point = findControlPoint(history.state, action.id);
      const moved =
        point !== undefined &&
        (point.position[0] !== drag.from[0] ||
          point.position[1] !== drag.from[1] ||
          point.position[2] !== drag.from[2]);
      if (!point || !moved) return { ...session, drag: undefined };
      return {
        history: record(
          history,
          moveControlPointCommand(action.id, drag.from, point.position),
          history.state,
        ),
        drag: undefined,
      };
    }
    case 'update': {
      const command = drag
        ? undefined
        : updateControlPointCommand(history.state, action.id, action.patch);
      return command ? { ...session, history: execute(history, command) } : session;
    }
    case 'set-closed': {
      const command = drag ? undefined : setClosedCommand(history.state, action.closed);
      return command ? { ...session, history: execute(history, command) } : session;
    }
    case 'set-vehicle': {
      const command = drag ? undefined : setVehicleCommand(history.state, action.id);
      return command ? { ...session, history: execute(history, command) } : session;
    }
    case 'set-environment': {
      const command = drag ? undefined : setEnvironmentCommand(history.state, action.id);
      return command ? { ...session, history: execute(history, command) } : session;
    }
    case 'rename': {
      const command = drag ? undefined : renameCourseCommand(history.state, action.name);
      return command ? { ...session, history: execute(history, command) } : session;
    }
    case 'add': {
      const command = drag ? undefined : addControlPointCommand(history.state, action.afterId);
      return command ? { ...session, history: execute(history, command) } : session;
    }
    case 'remove': {
      const command = drag ? undefined : removeControlPointCommand(history.state, action.id);
      return command ? { ...session, history: execute(history, command) } : session;
    }
    case 'undo':
      return drag ? session : { ...session, history: undo(history) };
    case 'redo':
      return drag ? session : { ...session, history: redo(history) };
    case 'replace-course':
      return createSession(action.course);
  }
}
